import React, { useEffect, useState } from 'react';
import {
  getPlans,
  getUserSubscriptions,
  checkoutSubscription,
  redeemCode,
} from '../../services/subscriptionService';
import {
  getCreditsStatus,
  createOneTimePayment,
  confirmPayment,
} from '../../services/creditsService';
import SeoPageComponent from '../../components/SeoPageComponent/SeoPageComponent';
import Breadcrumbs from '../../components/Breadcrumbs';
import Cookies from 'js-cookie';
import Utility from './../../utils/Utility';
import TermsModal from '../../components/TermsModal/TermsModal';
import InviteFormModal from '../../components/InviteFormModal/InviteFormModal';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  CheckCircle,
  AlertCircle,
  CreditCard,
  Shield,
  Zap,
  BarChart3,
  Bell,
  Headphones,
  Star,
  Sparkles,
  Gift,
  Tag,
} from 'lucide-react';

const FEATURES_ICONS = [
  { icon: Shield, color: 'text-indigo-500' },
  { icon: Sparkles, color: 'text-amber-500' },
  { icon: BarChart3, color: 'text-emerald-500' },
  { icon: Zap, color: 'text-blue-500' },
  { icon: Bell, color: 'text-purple-500' },
  { icon: Headphones, color: 'text-pink-500' },
  { icon: Star, color: 'text-orange-500' },
];

const Subscriptions = ({ setIsAuthenticated }) => {
  const [plans, setPlans] = useState([]);
  const [activeSubscription, setActiveSubscription] = useState(null);
  const [error, setError] = useState(null);
  const [isYearly, setIsYearly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [creditsData, setCreditsData] = useState(null);
  const [selectedPacks, setSelectedPacks] = useState(1);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [purchaseResult, setPurchaseResult] = useState(null);
  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoResult, setPromoResult] = useState(null);
  // Inline "Termenii si Conditiile" trigger opens the existing TermsModal
  // (no real /termeni-si-conditii route — it used to silently 404 in a new tab).
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const [isInviteOpen, setIsInviteOpen] = useState(false);

  const isAuth = Utility.getUserAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const PACK_PRICE = 490;
  const CREDITS_PER_PACK = 500;

  useEffect(() => {
    if (typeof window !== 'undefined') window.prerenderReady = false;
    const timeout = setTimeout(() => {
      if (typeof window !== 'undefined') window.prerenderReady = true;
    }, 2000);
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const token = Cookies.get('jwtToken');
      setIsAuthenticated(!!token);
      try {
        const response = await getPlans();
        setPlans(
          response
            ? response.filter(
                (p) => p.enable !== false && p.status !== false,
              )
            : [],
        );
      } catch (err) {
        console.error(err);
      }
      if (token) {
        try {
          const subs = await getUserSubscriptions();
          if (subs?.length > 0) {
            const active = subs.find(
              (s) => s.enable && new Date(s.endDate) > new Date(),
            );
            if (active) setActiveSubscription(active);
          }
        } catch {}
        try {
          const c = await getCreditsStatus();
          if (c.success) setCreditsData(c);
        } catch {}
      }
    };
    fetchData();
  }, [setIsAuthenticated]);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      (async () => {
        try {
          const result = await confirmPayment(sessionId);
          setPurchaseResult({
            success: result.success,
            message: result.success
              ? 'Creditele au fost adaugate cu succes!'
              : result.message || 'Eroare la confirmarea platii.',
          });
          if (result.success) {
            const c = await getCreditsStatus();
            if (c.success) setCreditsData(c);
            window.dispatchEvent(new CustomEvent('creditsUpdated'));
          }
        } catch {
          setPurchaseResult({
            success: false,
            message: 'Eroare la confirmarea platii.',
          });
        }
        navigate('/abonamente', { replace: true });
      })();
    }
  }, [searchParams, navigate]);

  const monthlyPlan = plans.find((p) => p.interval === 'month');
  const yearlyPlan = plans.find((p) => p.interval === 'year');
  const currentPlan = isYearly ? yearlyPlan : monthlyPlan;
  const monthlyPrice = monthlyPlan?.price || 99;
  const yearlyPrice = yearlyPlan?.price || 990;
  const yearlySavings = monthlyPrice * 12 - yearlyPrice;
  const isActivePlan =
    activeSubscription?.subscriptionId === currentPlan?.id;

  const handleSubscribe = async () => {
    if (!currentPlan) return;
    setLoading(true);
    setError(null);
    try {
      const response = await checkoutSubscription(
        Cookies.get('userId'),
        currentPlan.id,
      );
      if (response.url) window.location.replace(response.url);
      else setError('Nu s-a putut genera link-ul de plata.');
    } catch (err) {
      setError(err.message || 'Eroare la procesarea abonamentului.');
    } finally {
      setLoading(false);
    }
  };

  const handleRedeemCode = async () => {
    if (!promoCode.trim()) return;
    if (!isAuth) return navigate('/login');
    setPromoLoading(true);
    setPromoResult(null);
    try {
      const result = await redeemCode(promoCode.trim());
      setPromoResult({ success: true, message: result.message });
      setPromoCode('');
      // Refresh credits & subscription
      try {
        const c = await getCreditsStatus();
        if (c.success) setCreditsData(c);
        window.dispatchEvent(new CustomEvent('creditsUpdated'));
      } catch {}
      try {
        const subs = await getUserSubscriptions();
        if (subs?.length > 0) {
          const active = subs.find((s) => s.enable && new Date(s.endDate) > new Date());
          if (active) setActiveSubscription(active);
        }
      } catch {}
    } catch (err) {
      setPromoResult({ success: false, message: err.message });
    } finally {
      setPromoLoading(false);
    }
  };

  const handleBuyCredits = async () => {
    if (!selectedPacks || selectedPacks < 1 || !acceptTerms) return;
    setIsProcessing(true);
    setPurchaseResult(null);
    try {
      const response = await createOneTimePayment({
        quantity: selectedPacks,
        unit: 'pack',
        currency: 'RON',
        acceptTerms: true,
      });
      if (response.success && response.url) window.location.href = response.url;
      else
        setPurchaseResult({
          success: false,
          message: response.message || 'Eroare la crearea sesiunii de plata.',
        });
    } catch {
      setPurchaseResult({
        success: false,
        message: 'Eroare la procesarea platii.',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <>
      <SeoPageComponent
        pageTitle="Abonamente - Licitatii-Constructii"
        pageDescription="Alege un abonament sau cumpara credite pentru acces la licitatii publice."
        pageKeywords="abonamente licitatii, credite Licitatii-Constructii"
        pageUrl={`${import.meta.env.VITE_DOMAIN}/abonamente`}
      />

      <section className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-indigo-50 py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Breadcrumbs
            breadcrumbLinks={[
              { href: '/', label: 'Home' },
              { label: 'Abonamente' },
            ]}
          />

          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-black text-gray-900 mb-4 tracking-tight">
              Planul potrivit pentru{' '}
              <span className="bg-gradient-to-r from-indigo-600 to-emerald-500 bg-clip-text text-transparent">
                succesul tau
              </span>
            </h1>
            <p className="text-lg text-gray-500 max-w-2xl mx-auto">
              Acces complet la licitatii, rapoarte AI Premium si oferte tehnice
              personalizate.
            </p>
          </div>

          {/* Alerts */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-5 py-3 rounded-2xl mb-6 text-center text-sm max-w-lg mx-auto flex items-center justify-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}

          {activeSubscription && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl px-5 py-3 mb-8 max-w-lg mx-auto flex items-center gap-3">
              <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-emerald-800">
                  Abonament activ
                </p>
                <p className="text-xs text-emerald-600">
                  Valabil pana la{' '}
                  {new Date(activeSubscription.endDate).toLocaleDateString(
                    'ro-RO',
                    { day: 'numeric', month: 'long', year: 'numeric' },
                  )}
                </p>
              </div>
            </div>
          )}

          {/* Main grid */}
          <div className="grid lg:grid-cols-3 gap-8 items-start">
            {/* ===== SUBSCRIPTION CARD ===== */}
            <div className="lg:col-span-2">
              <div className="relative bg-white rounded-3xl shadow-2xl shadow-indigo-100/50 border border-gray-100 overflow-hidden">
                {/* Decorative gradient bar */}
                <div className="h-1.5 bg-gradient-to-r from-indigo-500 via-purple-500 to-emerald-500" />

                <div className="p-8 md:p-10">
                  {/* Toggle */}
                  <div className="flex items-center justify-center mb-8">
                    <div className="inline-flex items-center bg-gray-100 rounded-full p-1">
                      <button
                        onClick={() => setIsYearly(false)}
                        className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${
                          !isYearly
                            ? 'bg-white text-indigo-700 shadow-md'
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        Lunar
                      </button>
                      <button
                        onClick={() => setIsYearly(true)}
                        className={`px-5 py-2 rounded-full text-sm font-semibold transition-all flex items-center gap-1.5 ${
                          isYearly
                            ? 'bg-white text-indigo-700 shadow-md'
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        Anual
                        <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-bold">
                          -{Math.round((yearlySavings / (monthlyPrice * 12)) * 100)}%
                        </span>
                      </button>
                    </div>
                  </div>

                  {/* Price */}
                  <div className="text-center mb-8">
                    <div className="flex items-end justify-center gap-1">
                      <span className="text-6xl font-black text-gray-900 leading-none">
                        {currentPlan?.price || (isYearly ? 990 : 99)}
                      </span>
                      <div className="flex flex-col items-start mb-1">
                        <span className="text-lg font-bold text-gray-400">
                          RON
                        </span>
                        <span className="text-sm text-gray-400">
                          /{isYearly ? 'an' : 'luna'}
                        </span>
                      </div>
                    </div>

                    {isYearly && (
                      <p className="text-sm text-emerald-600 font-medium mt-2">
                        Economisesti {yearlySavings} RON pe an
                      </p>
                    )}

                    {currentPlan?.credits > 0 && (
                      <div className="mt-3 inline-flex items-center gap-1.5 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 text-indigo-700 text-sm font-semibold px-4 py-1.5 rounded-full">
                        <Sparkles className="w-4 h-4" />
                        {currentPlan.credits}{' '}
                        {currentPlan.credits === 1 ? 'credit' : 'credite'}{' '}
                        incluse
                      </div>
                    )}
                  </div>

                  {/* Features grid */}
                  <div className="grid sm:grid-cols-2 gap-3 mb-8 max-w-lg mx-auto">
                    {(currentPlan?.features || []).map((f, i) => {
                      const IconData =
                        FEATURES_ICONS[i % FEATURES_ICONS.length];
                      const Icon = IconData.icon;
                      return (
                        <div
                          key={i}
                          className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3"
                        >
                          <Icon
                            className={`w-5 h-5 ${IconData.color} flex-shrink-0`}
                          />
                          <span className="text-sm text-gray-700 font-medium">
                            {f}
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  {/* CTA */}
                  {isAuth ? (
                    isActivePlan ? (
                      <div className="w-full text-center bg-emerald-50 border-2 border-emerald-200 text-emerald-700 py-4 rounded-2xl font-bold text-lg flex items-center justify-center gap-2">
                        <CheckCircle className="w-5 h-5" />
                        Planul tau activ
                      </div>
                    ) : (
                      <button
                        onClick={handleSubscribe}
                        disabled={loading}
                        className="w-full bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 disabled:from-gray-400 disabled:to-gray-400 text-white font-bold py-4 rounded-2xl text-lg transition-all shadow-lg shadow-indigo-200 hover:shadow-xl hover:shadow-indigo-300 flex items-center justify-center gap-2"
                      >
                        {loading ? (
                          <>
                            <svg
                              className="animate-spin h-5 w-5"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              />
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                              />
                            </svg>
                            Procesare...
                          </>
                        ) : (
                          <>Incepe acum &rarr;</>
                        )}
                      </button>
                    )
                  ) : (
                    <button
                      type="button"
                      onClick={() => setIsInviteOpen(true)}
                      className="block w-full text-center bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white font-bold py-4 rounded-2xl text-lg shadow-lg transition-all"
                    >
                      Abonează-te
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* ===== CREDITS CARD ===== */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-3xl shadow-xl shadow-gray-100/50 border border-gray-100 overflow-hidden sticky top-24">
                <div className="h-1.5 bg-gradient-to-r from-amber-400 via-orange-400 to-rose-400" />

                <div className="p-6">
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                      <Zap className="w-4 h-4 text-amber-600" />
                    </div>
                    <h2 className="text-lg font-bold text-gray-800">
                      Credite Extra
                    </h2>
                  </div>
                  <p className="text-xs text-gray-400 text-center mb-5">
                    Nu expira. Rapoarte AI & oferte tehnice.
                  </p>

                  {purchaseResult && (
                    <div
                      className={`mb-4 p-3 rounded-xl text-sm flex items-center gap-2 ${
                        purchaseResult.success
                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                          : 'bg-red-50 text-red-700 border border-red-200'
                      }`}
                    >
                      {purchaseResult.success ? (
                        <CheckCircle className="w-4 h-4 flex-shrink-0" />
                      ) : (
                        <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      )}
                      <span className="text-xs">{purchaseResult.message}</span>
                    </div>
                  )}

                  {isAuth ? (
                    <>
                      {/* Quantity (packs) */}
                      <div className="flex items-center justify-center gap-2 mb-4">
                        <button
                          onClick={() =>
                            setSelectedPacks(Math.max(1, selectedPacks - 1))
                          }
                          disabled={isProcessing}
                          className="w-10 h-10 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 rounded-xl text-lg font-bold text-gray-600 transition"
                        >
                          -
                        </button>
                        <div className="relative">
                          <input
                            type="number"
                            min="1"
                            max="100"
                            value={selectedPacks}
                            onChange={(e) =>
                              setSelectedPacks(
                                Math.max(1, parseInt(e.target.value) || 1),
                              )
                            }
                            disabled={isProcessing}
                            className="w-20 h-12 text-center text-2xl font-black border-2 border-gray-200 rounded-xl focus:border-indigo-400 focus:outline-none bg-gray-50"
                          />
                        </div>
                        <button
                          onClick={() =>
                            setSelectedPacks(Math.min(100, selectedPacks + 1))
                          }
                          disabled={isProcessing}
                          className="w-10 h-10 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 rounded-xl text-lg font-bold text-gray-600 transition"
                        >
                          +
                        </button>
                      </div>

                      {/* Price */}
                      <div className="text-center mb-5 bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl py-4 px-3 border border-amber-100">
                        <p className="text-xs text-amber-600 font-medium mb-1">
                          {PACK_PRICE} RON / pachet ({CREDITS_PER_PACK} credite)
                        </p>
                        <p className="text-3xl font-black text-gray-900">
                          {(selectedPacks * PACK_PRICE).toLocaleString()}{' '}
                          <span className="text-lg font-bold text-gray-400">
                            RON
                          </span>
                        </p>
                        <p className="text-xs text-amber-600 font-medium mt-1">
                          ={' '}
                          {(
                            selectedPacks * CREDITS_PER_PACK
                          ).toLocaleString()}{' '}
                          credite
                        </p>
                      </div>

                      {/* Terms */}
                      <label
                        htmlFor="acceptTerms"
                        className="flex items-center gap-2 mb-4 p-2.5 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition"
                      >
                        <input
                          type="checkbox"
                          id="acceptTerms"
                          checked={acceptTerms}
                          onChange={(e) => setAcceptTerms(e.target.checked)}
                          disabled={isProcessing}
                          className="h-4 w-4 text-indigo-600 rounded border-gray-300"
                        />
                        <span className="text-xs text-gray-500">
                          Accept{' '}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              setIsTermsOpen(true);
                            }}
                            className="text-indigo-600 underline bg-transparent border-0 p-0 cursor-pointer"
                          >
                            Termenii si Conditiile
                          </button>
                        </span>
                      </label>

                      {/* Buy button */}
                      <button
                        onClick={handleBuyCredits}
                        disabled={isProcessing || !acceptTerms}
                        className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 disabled:from-gray-300 disabled:to-gray-300 text-white font-bold py-3 rounded-2xl transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                      >
                        {isProcessing ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                            Procesez...
                          </>
                        ) : (
                          <>
                            <CreditCard className="h-4 w-4" />
                            Cumpara {selectedPacks}{' '}
                            {selectedPacks === 1 ? 'pachet' : 'pachete'}
                          </>
                        )}
                      </button>
                    </>
                  ) : (
                    <div className="text-center py-6">
                      <p className="text-sm text-gray-500 mb-3">
                        Autentifica-te pentru a cumpara credite
                      </p>
                      <a
                        href="/login"
                        className="text-indigo-600 font-semibold hover:underline text-sm"
                      >
                        Login &rarr;
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cod Promotional */}
      <section className="bg-gradient-to-r from-emerald-50 via-teal-50 to-cyan-50 py-10 border-t border-emerald-100">
        <div className="max-w-xl mx-auto px-4 sm:px-6">
          <div className="bg-white rounded-2xl shadow-lg border border-emerald-100 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                <Tag className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-800">Cod Promotional</h3>
                <p className="text-xs text-gray-400">
                  Ai primit un cod la un eveniment sau webinar? Activeaza-l aici.
                </p>
              </div>
            </div>

            {promoResult && (
              <div
                className={`mb-4 p-3 rounded-xl text-sm flex items-center gap-2 ${
                  promoResult.success
                    ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}
              >
                {promoResult.success ? (
                  <CheckCircle className="w-4 h-4 flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                )}
                {promoResult.message}
              </div>
            )}

            <div className="flex gap-2">
              <input
                type="text"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === 'Enter' && handleRedeemCode()}
                placeholder="ex: WEBINAR2026"
                disabled={promoLoading || !isAuth}
                className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl text-sm font-mono font-semibold tracking-wider focus:border-emerald-400 focus:outline-none bg-gray-50 placeholder:text-gray-300 placeholder:font-normal placeholder:tracking-normal disabled:opacity-50"
              />
              <button
                onClick={handleRedeemCode}
                disabled={promoLoading || !promoCode.trim() || !isAuth}
                className="px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 disabled:from-gray-300 disabled:to-gray-300 text-white font-semibold rounded-xl text-sm transition-all flex items-center gap-2 whitespace-nowrap"
              >
                {promoLoading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                ) : (
                  <Gift className="w-4 h-4" />
                )}
                {promoLoading ? 'Se activeaza...' : 'Activeaza'}
              </button>
            </div>

            {!isAuth && (
              <p className="text-xs text-gray-400 mt-2 text-center">
                <a href="/login" className="text-emerald-600 font-semibold hover:underline">
                  Autentifica-te
                </a>{' '}
                pentru a activa un cod promotional.
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="bg-white py-16 border-t border-gray-100">
        <div className="container max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-3">
            Solutie personalizata?
          </h2>
          <p className="text-gray-500 mb-6">
            Pachete adaptate nevoilor organizatiei dumneavoastra.
          </p>
          <a
            href="/contact"
            className="inline-block bg-gray-900 hover:bg-gray-800 text-white font-bold py-3 px-8 rounded-2xl transition-all"
          >
            Contactati-ne
          </a>
        </div>
      </section>
      <TermsModal isOpen={isTermsOpen} onClose={() => setIsTermsOpen(false)} />
      <InviteFormModal isOpen={isInviteOpen} onClose={() => setIsInviteOpen(false)} />
    </>
  );
};

export default Subscriptions;
